import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Heart,
  Zap,
  Shield,
  Users,
  Star,
  Scissors,
  CheckCircle2,
  Instagram,
} from "lucide-react";

const team = [
  {
    name: "Equipe de Produto",
    role: "Desenvolvimento & Design",
    desc: "Produto, design e engenharia voltados para fluxos de atendimento e gestão de salões.",
    icon: Zap,
    color: "text-amber-400",
    bg: "bg-amber-400/10",
  },
  {
    name: "Implantação guiada",
    role: "Configuração & Onboarding",
    desc: "Recursos para organizar a configuração inicial de serviços, equipe, horários e regras de atendimento.",
    icon: Heart,
    color: "text-rose-400",
    bg: "bg-rose-400/10",
  },
  {
    name: "Automação e IA",
    role: "Conversas & Rotina",
    desc: "Recursos de IA e automação que podem ser configurados conforme os canais e as regras de cada salão.",
    icon: Shield,
    color: "text-primary",
    bg: "bg-primary/10",
  },
];

const values = [
  { title: "Simplicidade acima de tudo", desc: "A navegação prioriza os fluxos de agenda, clientes, equipe e caixa." },
  { title: "Tecnologia a serviço da beleza", desc: "Usamos IA e automação para que você passe mais tempo com seus clientes e menos com burocracia." },
  { title: "Crescimento junto com você", desc: "Do salão solo à rede de unidades, o sistema cresce conforme o seu negócio." },
  { title: "Implantação estruturada", desc: "A Academia e o onboarding organizam as etapas de configuração da operação." },
];

const productAreas = [
  { label: "Agenda", event: "Agendamentos, disponibilidade, equipe e regras de atendimento." },
  { label: "Relacionamento", event: "Clientes, pacotes, créditos, campanhas e retenção consentida." },
  { label: "Operação", event: "Caixa, comissões, estoque, fila, check-in e painel de chamada." },
  { label: "Gestão", event: "Filiais, relatórios, permissões e indicadores operacionais." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* ── Navigation ────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/8 bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-16">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <img
                src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-dark_41784aae.png"
                alt="Beleza & Bronze"
                className="h-8 w-auto object-contain"
              />
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/60">
            <Link href="/#features" className="hover:text-white transition-colors">Funcionalidades</Link>
            <Link href="/#pricing" className="hover:text-white transition-colors">Planos</Link>
            <Link href="/sobre" className="text-white font-medium">Sobre</Link>
          </div>
          <Link href="/admin">
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium">
              Meu Painel <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="pt-32 pb-20 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto relative z-10">
          <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">Nossa história</Badge>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 font-[Cormorant_Garamond,serif]">
            Gestão pensada para a rotina de{" "}
            <span className="text-primary">beleza e bem-estar</span>
          </h1>
          <p className="text-lg text-white/60 leading-relaxed max-w-2xl mx-auto">
            O Beleza & Bronze reúne ferramentas para organizar agenda, relacionamento, operação e gestão em um só ambiente.
          </p>
        </div>
      </section>

      {/* ── Story ─────────────────────────────────────────────────── */}
      <section className="py-20 px-4 border-y border-white/8 bg-white/2">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">A origem</Badge>
              <h2 className="text-3xl font-bold tracking-tight mb-6 font-[Cormorant_Garamond,serif]">
                  Recursos conectados para a operação do salão
              </h2>
              <p className="text-white/60 leading-relaxed mb-4">
                A plataforma foi estruturada para apoiar os fluxos que se repetem na rotina: reserva, confirmação, atendimento, recebimento e retorno.
              </p>
              <p className="text-white/60 leading-relaxed mb-4">
                Cada salão pode configurar serviços, profissionais, horários, permissões e canais conforme sua própria operação.
              </p>
              <p className="text-white/60 leading-relaxed">
                O resultado é uma base operacional com agenda online, recursos de IA configuráveis e controles financeiros integrados.
              </p>
            </div>
            <div className="space-y-3">
              {productAreas.map((m) => (
                <div key={m.label} className="flex gap-4 items-start">
                  <div className="shrink-0 w-12 text-right">
                    <span className="text-xs font-bold text-primary">{m.label}</span>
                  </div>
                  <div className="flex-1 border-l border-white/10 pl-4 pb-3">
                    <p className="text-sm text-white/70">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission ───────────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">Missão</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 font-[Cormorant_Garamond,serif]">
            Democratizar a tecnologia para salões de beleza
          </h2>
          <p className="text-xl text-white/50 leading-relaxed max-w-2xl mx-auto mb-12">
            Acreditamos que todo salão, do pequeno ao grande, merece ter acesso às mesmas ferramentas 
            que grandes redes usam — sem complexidade e sem preço absurdo.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            {values.map((v) => (
              <div key={v.title} className="flex gap-3 p-5 rounded-2xl border border-white/8 bg-white/2 hover:bg-white/4 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white mb-1">{v.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Áreas do sistema ───────────────────────────────────────── */}
      <section className="py-16 px-4 border-y border-white/8 bg-white/2">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
              { value: "Agenda", label: "horários e disponibilidade" },
              { value: "Clientes", label: "relacionamento e pacotes" },
              { value: "Operação", label: "caixa, fila e atendimento" },
              { value: "Gestão", label: "dados por salão" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-white mb-1">{s.value}</div>
                <div className="text-sm text-white/40">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team ──────────────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">Equipe</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-[Cormorant_Garamond,serif]">
              Quem está por trás do sistema
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((t) => (
              <div key={t.name} className="rounded-2xl border border-white/8 bg-white/2 p-6 hover:bg-white/4 transition-colors">
                <div className={`w-12 h-12 rounded-xl ${t.bg} flex items-center justify-center mb-4`}>
                  <t.icon className={`w-6 h-6 ${t.color}`} />
                </div>
                <h3 className="font-semibold text-white mb-1">{t.name}</h3>
                <p className="text-xs text-primary mb-3">{t.role}</p>
                <p className="text-sm text-white/50 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social ────────────────────────────────────────────────── */}
      <section className="py-16 px-4 border-t border-white/8 bg-white/2">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold tracking-tight mb-3">Siga nossa jornada</h2>
          <p className="text-white/50 mb-6">
            Acompanhe novidades, dicas para salões e bastidores do produto no Instagram.
          </p>
          <a
            href="https://instagram.com/belezaebronzeapp"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 bg-transparent">
              <Instagram className="w-4 h-4 mr-2" />
              @belezaebronzeapp no Instagram
            </Button>
          </a>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="rounded-3xl border border-primary/30 bg-primary/10 p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <Scissors className="w-8 h-8 text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-bold tracking-tight mb-4">
                Pronto para transformar seu salão?
              </h2>
              <p className="text-white/60 mb-8">
                Estruture a agenda, a equipe e os controles da sua operação em um só lugar.
              </p>
              <Link href="/onboarding">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 h-12 text-base font-medium shadow-lg shadow-primary/30">
                  Começar grátis agora
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-white/30 mt-4">
                Sem cartão de crédito · Cancele quando quiser
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="border-t border-white/8 py-10 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img
            src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-dark_41784aae.png"
            alt="Beleza & Bronze"
            className="h-7 w-auto object-contain"
          />
          <div className="flex gap-6 text-sm text-white/40">
            <Link href="/" className="hover:text-white transition-colors">Início</Link>
            <Link href="/#features" className="hover:text-white transition-colors">Funcionalidades</Link>
            <Link href="/#pricing" className="hover:text-white transition-colors">Planos</Link>
          </div>
          <p className="text-xs text-white/20">© 2026 Beleza & Bronze. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
